import React, { PureComponent } from 'react'
import Hammer from 'react-hammerjs'
import PropTypes from 'prop-types'
import autoBind from 'react-autobind'
import _ from 'lodash'
import styled from 'styled-components'
import Caption from './Caption'
import ImageToCanvas from '../ImageToCanvas'
import styles from './ImageSlider.module.css'

/**
 * Horizontal and endless image slider component with caption for the focused image
 */

export const STATUS_IDLE = 'STATUS_IDLE'
export const STATUS_PLAY_OUT = 'STATUS_PLAY_OUT'
export const STATUS_PLAY_IN = 'STATUS_PLAY_IN'
export const STATUS_SNAP = 'STATUS_SNAP'
export const STATUS_RESET = 'STATUS_RESET'
export const STATUS_PAN = 'STATUS_PAN'
export const STATUS_ROLL = 'STATUS_ROLL'

const withoutTransition = [STATUS_IDLE, STATUS_PAN]
const spyInterval = 500

const Helper = ({ text, offset = 0 }) => (
  <div
    style={{
      position: 'absolute',
      top: offset,
      background: 'black',
      color: 'white',
      padding: 10,
    }}
  >
    {text}
  </div>
)

const Canvas = styled(ImageToCanvas)`
  pointer-events: none;
`

class ImageSlider extends PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      status: STATUS_IDLE,
      scrollPos: 0,
      targetPos: 0,
      scrollPosEstimated: 0,
      duration: 0,
      easing: 'linear',
      visible: [],
    }

    autoBind(this)

    // Stores the delta between 'pan' events for dragging
    this.dx = 0

    this.getIndexFromPos = _.memoize(this.getIndexFromPos, pos =>
      Math.round(pos / 10)
    )

    this.getVisibleFromPos = _.memoize(this.getVisibleFromPos, pos =>
      Math.round(pos / 10)
    )
  }

  memoLayout = _.memoize((height, width, spacing, images) => {
    const sliderHeight = height
    const sliderWidth = width
    const scaledImages = images.map(({ dimension: { width, height } }) => {
      const ratio = height / sliderHeight
      return {
        width: width / ratio,
        height: height / ratio,
      }
    })

    // Otherwise the caption won`t be accessible
    const totalWidth =
      _.sumBy(scaledImages, 'width') + (images.length + 1) * spacing

    // prevent gap at the right side of the slider
    const maxDisplacement = totalWidth - sliderWidth

    const stops = _.transform(
      scaledImages,
      (result, n, i, a) => {
        let stop =
          i === 0
            ? spacing
            : Math.min(
                result[i - 1] + a[i - 1].width + spacing,
                maxDisplacement
              )

        // if the last image`s width exceeds the slider width,
        // push the stop to the left in order to continue scrolling
        // until the right border of the image reaches the end of the slider
        if (i === a.length - 1 && a[i].width > sliderWidth) {
          stop += a[i].width - sliderWidth
        }
        result.push(Math.round(stop))
      },
      []
    )

    return { stops, scaledImages, totalWidth }
  })

  getLayout() {
    const { height, width, spacing, images } = this.props
    return this.memoLayout(height, width, spacing, images)
  }

  componentDidMount() {
    // Component has to be rendered first in stopped state to prepare
    // the transition, then it can switch to play mode
    if (this.props.autoPlay) {
      this.timeout = setTimeout(() => {
        this.play()
      }, this.props.autoDelay)
    }
  }

  componentWillUnmount() {
    clearInterval(this.interval)
    clearTimeout(this.timeout)
  }

  get inside() {
    const { scrollPos } = this.state
    return this.positionToBounds(scrollPos) === scrollPos
  }

  get visible() {
    const { scrollPos } = this.state
    const v1 = this.getVisibleFromPos(scrollPos)
    return _.orderBy(v1, 'distance')
  }

  get interactive() {
    const { totalWidth } = this.getLayout()
    return totalWidth > this.props.width
  }

  /**
   * Returns the transition parameters for the current
   * component state and distance from the old position to the new one.
   *
   * @param {string} status
   * @param {number} start
   * @param {number} end
   */
  getTransition(status, start, end) {
    const {
      rollAmount,
      rollSnappiness,
      snapSpeed,
      playSpeed,
      width,
    } = this.props

    const { totalWidth } = this.getLayout()
    const distance = Math.abs(start - end)

    if (distance === 0) {
      return {
        easing: 'linear',
        duration: 0,
      }
    }

    const x2 = _.clamp((2 * distance) / totalWidth, 0.05, 0.95)

    let duration
    let easing

    switch (status) {
      case STATUS_PLAY_IN:
      case STATUS_PLAY_OUT:
        easing = `linear` // FEATURE: (2) stop slideshow softly
        duration = distance / (playSpeed * 1000)
        break
      case STATUS_ROLL:
        easing = `cubic-bezier(0.000, 0.000, ${x2}, 1.000)`
        duration = distance / (rollSnappiness * rollAmount * width)
        break
      case STATUS_RESET:
        easing = `ease-in-out`
        duration = 0.5
        break
      case STATUS_SNAP:
        duration = Math.sqrt(distance / (snapSpeed * 1000))
        easing = 'cubic-bezier(0.175, 0.885, 0.5, 1)'
        break
      default:
        easing = `linear`
        duration = 0
    }

    return { duration: _.round(duration, 2), easing }
  }

  scrollTo(x, status, cb) {
    // Only allow interaction if images are wider than the stage
    if (_.isFinite(x) && this.interactive) {
      const { scrollPos } = this.state
      const isAnimated = withoutTransition.indexOf(status) === -1
      const end = Math.round(x)
      const start = isAnimated ? scrollPos : end
      const { easing, duration } = this.getTransition(status, start, end)
      this.setState(
        {
          scrollPos: start,
          targetPos: end,
          status: status,
          easing,
          duration,
        },
        cb
      )
    }
  }

  positionToBounds(x) {
    const { width } = this.props
    const { totalWidth } = this.getLayout()
    const lowerBound = -totalWidth + width
    const upperBound = 0
    return _.clamp(x, lowerBound, upperBound)
  }

  // TODO: (3) Use intersection observer
  startSpy() {
    const check = () => {
      if (this.state.status === STATUS_IDLE) {
        clearInterval(this.interval)
        this.interval = null
      } else {
        const pos = this.getTransitionX()
        if (_.isNumber(pos) && pos !== this.state.scrollPos) {
          this.setState({
            scrollPos: pos,
          })
        }
      }
    }

    if (!this.interval) this.interval = setInterval(check, spyInterval)
  }

  next() {
    const { stops } = this.getLayout()
    const newPos = stops[this.index + 1] * -1
    if (newPos) {
      this.scrollTo(newPos, STATUS_PLAY_OUT)
      this.startSpy()
    } else {
      this.timeout = setTimeout(this.reset, 2000)
    }
  }

  /**
   * Sets the new position of the slider from one image to the next.
   */
  play() {
    const { stops } = this.getLayout()
    const { index } = this
    // horizontal offset of the slider in px based on the current index
    const newPos = -stops[index]
    // horizontal offset of the slider in px to focus the next image/right neighbor of current image
    const nextPos = index < stops.length - 1 ? -stops[index + 1] : null
    // if slider was stopped inbetween to images, take the current image,
    // otherwise take the next image to calculate the offset
    const selectedPos = newPos < this.state.scrollPos ? newPos : nextPos

    if (selectedPos) this.scrollTo(selectedPos, STATUS_PLAY_OUT)
    this.startSpy()
  }

  /**
   * Sets the status to STATUS_IDLE and stores the current value of
   * the CSS transition to the component state. If the position is outside
   * state will be set to STATUS_SNAP. A callback can be passed,
   * which will be executed after the state had been update.
   *
   * @param {function} cb
   */
  stop(cb) {
    const newPos = this.getTransitionX()
    if (_.isFinite(newPos)) {
      const clamped = this.positionToBounds(newPos)
      const isInside = newPos === clamped
      const nextStatus = isInside ? STATUS_IDLE : STATUS_SNAP
      this.scrollTo(clamped, nextStatus, cb)
    }
  }

  reset() {
    this.scrollTo(0, STATUS_RESET)
  }

  /**
   * Adds `dx` to the current scroll position
   *
   * @param {number} dx
   */
  panTo(dx = 0) {
    const newPos = this.state.scrollPos + dx
    const bound = this.positionToBounds(newPos)
    const tolerance = this.props.width / 4
    const min = bound - tolerance
    const max = bound + tolerance
    const x = _.clamp(newPos, min, max)

    this.scrollTo(x, STATUS_PAN)
  }

  handlePan(e) {
    const { status, scrollPos } = this.state
    const { rollAmount, width } = this.props
    const x = e.deltaX - this.dx
    this.dx = e.deltaX

    if (status !== STATUS_PAN) {
      this.stop(this.panTo)
    } else if (e.isFinal) {
      if (Math.abs(e.velocityX) > 0.3) {
        const x = scrollPos + e.velocityX * rollAmount * width
        const newPos = this.positionToBounds(x)
        this.scrollTo(newPos, STATUS_ROLL)
        this.startSpy()
      } else {
        this.stop()
      }
      this.dx = 0
    } else {
      this.panTo(x)
    }
  }

  /**
   * Toggles between PLAY and IDLE mode
   */
  handleTap() {
    if (
      this.state.status === STATUS_PLAY_OUT ||
      this.state.status === STATUS_PLAY_IN ||
      this.state.status === STATUS_ROLL ||
      this.state.status === STATUS_SNAP
    ) {
      this.stop()
    } else {
      this.play()
    }
  }

  getTransitionX() {
    try {
      const transform = window.getComputedStyle(this.container).transform
      return _.parseInt(transform.split(',')[4], 0)
    } catch (e) {
      console.warn('could not get element')
      return null
    }
  }

  getVisibleFromPos(pos) {
    const { width: sliderWidth } = this.props
    const { scaledImages, stops } = this.getLayout()
    const tolerance = 2
    return scaledImages
      .map(({ width }, index) => {
        const start = stops[index]
        const center = start + width / 2
        const distance = Math.abs(center + pos - sliderWidth / 2)
        return {
          start,
          width,
          distance,
          index,
        }
      })
      .filter(
        ({ distance, width }) =>
          distance - width / 2 < (tolerance * sliderWidth) / 2
      )
  }

  /**
   * Returns the memoized index for the current scroll position.
   */
  get index() {
    return this.props.images.length > 0
      ? this.getIndexFromPos(this.state.scrollPos)
      : null
  }

  /**
   * Returns the index for a specific scroll position.
   *
   * @param {number} scrollPos
   */
  getIndexFromPos(scrollPos) {
    const visible = this.visible
    if (scrollPos === 0 || visible.length === 0) return 0
    return _.orderBy(visible, 'distance')[0].index
  }

  renderContainer() {
    const { targetPos, easing, duration, status } = this.state
    const { totalWidth } = this.getLayout()
    return (
      <div
        ref={ref => {
          this.container = ref
        }}
        className={styles.Container}
        style={{
          transform: `translate3d(${targetPos}px, 0, 0)`,
          transition: `transform ${duration}s ${easing}`,
          justifyContent:
            totalWidth < this.props.width ? 'center' : 'flex-start',
        }}
        onTransitionEnd={x => {
          // the transitionEnd event could be from child nodes of the slider container,
          // thus, filtering is needed
          const isContainer = !/SliderItem/.test(x.target.className)
          if (isContainer) {
            this.stop(() => {
              // in play mode (status === STATUS_PLAY_OUT), the slider translates the container
              // step by step by calling the next() function each time the transition ends
              if (status === STATUS_PLAY_OUT) this.next()
            })
          }
        }}
      >
        {this.renderChildren()}
      </div>
    )
  }

  renderChildren() {
    const { images, spacing, height: sliderHeight, debug } = this.props
    const visible = this.visible
    return images.map(
      ({ imageID, url, dimension: { width: w, height: h } }, i) => {
        const ratio = h / sliderHeight
        const width = w / ratio
        const height = h / ratio
        const isVisible = visible.find(({ index }) => index === i)
        return (
          <div
            key={`${imageID}-${width}-${height}`}
            className={styles.SliderItem}
            style={{
              width,
              height,
              marginRight: i < images.length - 1 ? spacing : 0,
              marginLeft: i === 0 ? spacing : 0,
              border: i === this.index && debug ? '2px solid red' : 'none',
              opacity: i === this.index ? 1 : this.interactive ? 0.33 : 1,
              transition: 'opacity 0.7s ease-in-out',
            }}
          >
            <Canvas
              width={width}
              height={height}
              url={url}
              show={!!isVisible}
              floorFade={true}
            />
            {debug && (
              <div
                style={{
                  position: 'absolute',
                  top: 200,
                  padding: '1rem',
                  zIndex: 1,
                  background: 'red',
                }}
              >
                {isVisible ? 'visible' : 'not visible'}
              </div>
            )}
          </div>
        )
      }
    )
  }

  render() {
    const { status, targetPos, scrollPos } = this.state
    const {
      width,
      height,
      debug,
      images,
      showCaption,
      className,
      renderCaption,
    } = this.props
    const idx = this.index
    const text = showCaption && idx !== null ? images[idx].description : ''

    return (
      <div className={className} style={{ width }}>
        <div
          className={styles.ScrollPane}
          style={{ height, border: debug ? '1px solid black' : 'none' }}
        >
          <Hammer
            onPan={this.handlePan}
            onPress={() => {
              this.stop()
            }}
            onTap={this.handleTap}
            onClick={e => {
              e.stopPropagation()
            }}
          >
            {/* NOTE: This wrapper is needed to protect the 'ref'
            from being overwritten by the hammer component  */}
            <div>
              {this.renderContainer()}
              {debug && (
                <div>
                  <Helper text={status} />
                  <Helper text={`${targetPos} / ${scrollPos}`} offset={40} />
                  {idx > -1 && <Helper text={idx} offset={80} />}
                </div>
              )}
            </div>
          </Hammer>
        </div>
        {showCaption &&
          (renderCaption ? renderCaption(text) : <Caption text={text} />)}
      </div>
    )
  }
}

ImageSlider.defaultProps = {
  height: 200,
  width: 300,
  images: [],
  continuousPlay: false,
  playSpeed: 0.05,
  snapSpeed: 0.2,
  rollAmount: 1.5,
  rollSnappiness: 0.33,
  autoPlay: true,
  spacing: 8,
  autoDelay: 3000,
  debug: false,
  showCaption: true,
}

ImageSlider.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      dimension: PropTypes.shape({
        width: PropTypes.number,
        height: PropTypes.number,
      }),
      imageID: PropTypes.number,
    })
  ),
  width: PropTypes.number,
  height: PropTypes.number,
  continuousPlay: PropTypes.bool,
  playSpeed: PropTypes.number,
  snapSpeed: PropTypes.number,
  rollSpeed: PropTypes.number,
  rollSnappiness: PropTypes.number,
  autoplay: PropTypes.bool,
  spacing: PropTypes.number,
  autoDelay: PropTypes.number,
  debug: PropTypes.bool,
  showCaption: PropTypes.bool,
  renderCaption: PropTypes.func,
}

export default ImageSlider
